import { Globe, Palette, Megaphone, Sparkles } from "lucide-react";

const PILLARS = [
  {
    icon: Globe,
    accent: "cyan",
    title: "Custom — not cookie-cutter.",
    body: "Every business is different. Every website should be too. We design and build each site to fit your specific brand, your specific customers, and the way you actually do business — not a recycled template with a new logo.",
  },
  {
    icon: Palette,
    accent: "orange",
    title: "Design and feel are everything.",
    body: "Visitors decide whether to trust you within seconds. The layout, the typography, the color, the imagery — every detail signals whether you're a serious operation or an afterthought. We treat aesthetics as part of the work, not decoration.",
  },
  {
    icon: Megaphone,
    accent: "lime",
    title: "A site alone won't bring customers.",
    body: "A beautiful website is the foundation, not the finish line. Real growth requires reaching the right audience and giving them a reason to choose you. We pair every build with the marketing and advertising strategy that turns visibility into revenue.",
  },
];

const ACCENT = {
  cyan: { border: "border-neon-cyan/40", text: "text-neon-cyan", bg: "bg-neon-cyan/5" },
  orange: { border: "border-neon-orange/40", text: "text-neon-orange", bg: "bg-neon-orange/5" },
  lime: { border: "border-neon-lime/40", text: "text-neon-lime", bg: "bg-neon-lime/5" },
};

export default function WhyWebsite() {
  return (
    <section data-testid="why-website-section" className="relative py-24 md:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Lead */}
        <div className="grid lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-5">
            <div className="overline mb-4 flex items-center gap-2">
              <Sparkles size={14} /> // WHY_IT_MATTERS_NOW
            </div>
            <h2 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
              Your website<br />
              is your <span className="text-neon-cyan neon-text-cyan">storefront</span>.
            </h2>
          </div>
          <div className="lg:col-span-7 max-w-2xl">
            <p className="text-white/70 text-lg md:text-xl leading-relaxed mb-5">
              In today&apos;s market, your website is doing more than describing your business — it&apos;s deciding whether someone <span className="text-white">trusts you enough to spend money with you.</span> It opens before you do. It closes after you leave. It works in cities you&apos;ve never visited.
            </p>
            <p className="text-white/60 text-base md:text-lg leading-relaxed">
              If you&apos;re serious about growth, you can&apos;t afford for it to be slow, generic, or invisible. A premium website built by <span className="text-white">Techsavvypreneurs.com</span> is engineered to attract, convince, and convert — and paired with the marketing strategy required to actually be found.
            </p>
          </div>
        </div>

        {/* Pillars */}
        <div className="grid md:grid-cols-3 gap-px bg-white/10">
          {PILLARS.map((p, i) => {
            const a = ACCENT[p.accent];
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                data-testid={`pillar-${i}`}
                className="group bg-[#05050A] p-8 md:p-10 hover:bg-white/[0.03] transition-all relative overflow-hidden"
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/30 mb-5">
                  // {String(i + 1).padStart(2, "0")}
                </div>
                <div className={`w-12 h-12 flex items-center justify-center border ${a.border} ${a.bg} mb-6`}>
                  <Icon size={20} strokeWidth={1.5} className={a.text} />
                </div>
                <h3 className="font-heading font-bold text-xl md:text-2xl tracking-tight mb-3 leading-tight">
                  {p.title}
                </h3>
                <p className="text-white/60 text-sm md:text-[15px] leading-relaxed">
                  {p.body}
                </p>
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-current to-transparent opacity-0 group-hover:opacity-60 transition-opacity" style={{ color: "currentColor" }} />
              </div>
            );
          })}
        </div>

        {/* Closing line */}
        <div className="mt-12 text-center max-w-3xl mx-auto">
          <p className="text-white/50 text-base md:text-lg italic">
            We don&apos;t just build websites. <span className="text-white not-italic">We build the digital foundation, the brand presence, and the visibility your business needs to grow on purpose.</span>
          </p>
        </div>
      </div>
    </section>
  );
}
